import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import { useIntl } from "react-intl";

import { usePredictionChart } from "@/api/predictions";
import { useTheme } from "@/hooks";
import { type PredictionStatus } from "@/schemas";
import { cn } from "@/utils/cn";

import {
  options as chartOptions,
  yAxisTopPlugin,
  legendSpacingPlugin,
  endpointPulsePlugin,
} from "./chart-options";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface ChartProps {
  status: PredictionStatus;
  size?: "small" | "big";
}

function PredictionChart({ status, size = "big" }: ChartProps) {
  const intl = useIntl();
  const theme = useTheme();

  const chartFilter = ["Moneyline", "Moneyline (3-Way)"].includes(
    status.prediction.contracts[0].type || "",
  )
    ? status.prediction.contracts[0].type
    : undefined;
  const { chart: data, isLoading } = usePredictionChart(status, {
    limit: 4,
    contract_types: chartFilter ? [chartFilter] : undefined,
  });

  const chartRef = useRef<ChartJS<"line"> | null>(null);

  const labels = data?.length
    ? data[0].datapoints.map(([ts]) =>
        intl.formatDate(new Date(ts), {
          month: "numeric",
          day: "numeric",
        }),
      )
    : [];

  const chartData = {
    labels,
    datasets: data?.length
      ? data.map((data) => {
          return {
            label: data.name,
            data: data.datapoints.map(([ts, value]) => ({
              x: ts * 1000,
              y: value,
            })),
            borderWidth: 1.5,
            borderColor:
              theme === "dark"
                ? `#${data.colors.dark || "868393"}`
                : `#${data.colors.light || "868393"}`,
            backgroundColor:
              theme === "dark"
                ? `#${data.colors.dark || "868393"}`
                : `#${data.colors.light || "868393"}`,
          };
        })
      : [],
  };

  return (
    <div className="h-full">
      {isLoading ? (
        <div
          className={cn(
            "w-full animate-pulse rounded-lg bg-primary-100 dark:bg-gray-800",
            {
              "h-[350px]": size === "big",
              "h-[320px]": size === "small",
            },
          )}
        />
      ) : (
        <Line
          key={`chart-${theme}`}
          ref={chartRef}
          options={chartOptions(theme)}
          plugins={[
            yAxisTopPlugin(theme),
            legendSpacingPlugin,
            endpointPulsePlugin,
          ]}
          data={chartData}
          height={size === "big" ? 350 : 290}
        />
      )}
    </div>
  );
}

export default React.memo(PredictionChart);
